import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EventCard from "@/components/feature/EventCard";
import { useSeo } from "@/hooks/useSeo";
import { getEventBySlug, getRelatedEvents, getTopEvents } from "@/utils/content";

export default function EventDetail() {
  const { slug = "" } = useParams();
  const event = getEventBySlug(slug);
  const related = useMemo(() => getRelatedEvents(slug, 3), [slug]);
  const top = getTopEvents(5);

  const jsonLd = useMemo(() => {
    if (!event) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: event.title,
        description: event.summary,
        image: event.cover,
        datePublished: event.date,
        dateModified: event.date,
        author: { "@type": "Organization", name: "1每日吃瓜" },
        publisher: { "@type": "Organization", name: "1每日吃瓜" },
        articleSection: event.category,
        keywords: event.tags.join(","),
        inLanguage: "zh-CN",
        mainEntityOfPage: `https://77di.cn/events/${event.slug}`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://77di.cn/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "热门事件",
            item: "https://77di.cn/events",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: event.title,
            item: `https://77di.cn/events/${event.slug}`,
          },
        ],
      },
    ];
  }, [event]);

  useSeo({
    title: event ? `${event.title} | 1每日吃瓜焦点事件` : "事件详情 | 1每日吃瓜",
    description: event
      ? `${event.summary} 1每日吃瓜为你梳理该焦点事件的完整脉络与关键节点。`
      : "1每日吃瓜焦点事件详情页。",
    keywords: event ? event.tags.join(",") : "1每日吃瓜,焦点事件",
    path: `/events/${slug}`,
    jsonLd,
  });

  if (!event) {
    return (
      <SiteLayout>
        <div className="w-full px-4 py-24 text-center md:px-8">
          <i className="ri-error-warning-line text-4xl text-primary-500"></i>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
            抱歉，这条事件不存在
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            它可能已被调整或链接有误，不如先看看当前的热门事件。
          </p>
          <Link
            to="/events"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回热门事件
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200/70 bg-background-100/60 px-4 py-10 md:px-8 md:py-14">
        <Breadcrumb items={[{ label: "热门事件", path: "/events" }, { label: event.category, path: `/events?cat=${event.categoryId}` }, { label: event.title }]} />
        <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-foreground-600">
          <span className="rounded-full bg-primary-100 px-3 py-1 font-medium text-primary-800">
            {event.category}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {event.date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            阅读约 {event.readTime}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500"></i>
            热度 {event.heat.toFixed(1)}
          </span>
        </div>
        <h1 className="mt-4 max-w-4xl font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
          {event.title}
        </h1>
      </div>

      <div className="grid w-full grid-cols-1 gap-10 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-12">
        <article className="lg:col-span-8">
          <div className="h-64 w-full overflow-hidden rounded-lg md:h-[420px]">
            <img
              src={event.cover}
              alt={`${event.title} 事件配图`}
              title={`${event.title} - 1每日吃瓜焦点事件`}
              className="h-full w-full object-cover"
            />
          </div>

          <p className="mt-7 border-l-4 border-primary-400 bg-primary-50/70 px-5 py-4 text-base leading-relaxed text-foreground-800">
            {event.summary}
          </p>

          <section className="mt-8" aria-labelledby="highlights-title">
            <h2 id="highlights-title" className="font-heading text-xl font-bold text-foreground-950">
              三个关键点
            </h2>
            <ul className="mt-4 space-y-3">
              {event.highlights.map((item) => (
                <li key={item} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-100 text-[11px] font-bold text-accent-700">
                    <i className="ri-check-line"></i>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-10" aria-labelledby="timeline-title">
            <h2 id="timeline-title" className="font-heading text-xl font-bold text-foreground-950">
              事件脉络
            </h2>
            <ol className="mt-5 space-y-5 border-l-2 border-background-200 pl-6">
              {event.timeline.map((node) => (
                <li key={node.time} className="relative">
                  <span className="absolute -left-[31px] top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-primary-500 bg-background-50"></span>
                  <p className="text-xs font-medium text-primary-700">{node.time}</p>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-700">{node.text}</p>
                </li>
              ))}
            </ol>
          </section>

          <section className="mt-10" aria-labelledby="body-title">
            <h2 id="body-title" className="font-heading text-xl font-bold text-foreground-950">
              事件梳理
            </h2>
            <div className="mt-4 space-y-5">
              {event.body.map((para) => (
                <p key={para.slice(0, 12)} className="text-base leading-loose text-foreground-700">
                  {para}
                </p>
              ))}
            </div>
          </section>

          <div className="mt-9 flex flex-wrap items-center gap-2 border-t border-background-200/80 pt-6">
            <span className="text-sm text-foreground-500">相关标签：</span>
            {event.tags.map((tag) => (
              <Link
                key={tag}
                to={`/events?q=${encodeURIComponent(tag)}`}
                className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <div className="mt-8 rounded-lg border border-background-200/80 bg-background-100/60 p-5">
            <p className="text-sm leading-relaxed text-foreground-600">
              <i className="ri-information-line mr-1 text-primary-500"></i>
              本文基于公开渠道已形成讨论的信息进行脉络梳理，旨在帮助读者理解事件走向，不代表对任何一方的评价。请理性看待网络信息。
            </p>
          </div>
        </article>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-6">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-fire-line text-primary-500"></i>
                今日热榜
              </h2>
              <ol className="mt-4 space-y-3">
                {top.map((item, idx) => (
                  <li key={item.slug} className="flex gap-3">
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                        idx < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <Link
                      to={`/events/${item.slug}`}
                      className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors hover:text-primary-700"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg bg-primary-500 p-5 text-background-50">
              <h2 className="font-heading text-base font-bold">订阅本周瓜单</h2>
              <p className="mt-2 text-sm leading-relaxed text-background-100">
                每周一次，把当周焦点事件一次性看完，不打扰、可随时取消。
              </p>
              <Link
                to="/about#subscribe"
                className="mt-4 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800"
              >
                <i className="ri-mail-add-line"></i>
                立即订阅
              </Link>
            </div>
          </div>
        </aside>
      </div>

      <section className="w-full border-t border-background-200/70 bg-background-100/60 px-4 py-14 md:px-8">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">相关推荐</h2>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((item) => (
            <EventCard
              key={item.slug}
              slug={item.slug}
              title={item.title}
              summary={item.summary}
              cover={item.cover}
              category={item.category}
              date={item.date}
              heat={item.heat}
            />
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}